import React, { useEffect, useState, useMemo } from 'react';

import {
  Button,
  PopUp,
  Dialog,
  Select,
  Input,
  ProgressBar,
  Tooltip,
  type OptionType,
  Label,
} from '@ergeon/core-components';
import { ArrowUturnLeftIcon, SpeakerWaveIcon, MicrophoneIcon } from '@ergeon/icons/hero/outline';

import { type ModalHandler } from '../index';
import useMediaDevices from '../../hooks/useMediaDevices';
import useMicrophoneTest from '../../hooks/useMicrophoneTest';
import useSpeakerTest from '../../hooks/useSpeakerTest';
import { settingsService } from '../../services';
import { LEVEL } from '../../envs';
import { useModalClose } from '../../hooks/useModalClose';

type Props = {modal: ModalHandler};

type SettingsType = {
  microphoneId: string;
  speakerId: string;
  baseLambdaEndpoint: string;
};

const SettingsModal: React.FC<Props> = ({ modal }) => {
  const { requestPermission, speakerDevices, microphoneDevices } = useMediaDevices();
  const { testMicrophone, stopTesting: stopMicTest, isTesting: isMicTesting, volumeLevel } = useMicrophoneTest();
  const { testSpeaker, isTesting: isSpeakerTesting, signalLevel } = useSpeakerTest();
  const closeModal = useModalClose(modal);

  const appSettings = settingsService.getSettings();

  // Current saved settings
  const [savedSettings, setSavedSettings] = useState<SettingsType>({
    microphoneId: appSettings.microphoneId,
    speakerId: appSettings.speakerId,
    baseLambdaEndpoint: appSettings.baseLambdaEndpoint,
  });

  // Pending changes (what user is editing)
  const [pendingSettings, setPendingSettings] = useState<SettingsType>({
    microphoneId: appSettings.microphoneId,
    speakerId: appSettings.speakerId,
    baseLambdaEndpoint: appSettings.baseLambdaEndpoint,
  });

  const onSettingsChange = (key: string, option: OptionType) => {
    const value = option.value;
    setPendingSettings({
      ...pendingSettings,
      [key]: value,
    });
  };

  const onInputChange = (key: string, value: string) => {
    setPendingSettings({
      ...pendingSettings,
      [key]: value,
    });
  };

  const onRestoreDefault = () => {
    const currentSavedSettings = settingsService.getSettings();
    const defaultEndpoint = settingsService.restoreDefaultBaseLambdaEndpoint();

    // Update both saved and pending settings
    const newSettings = {
      ...currentSavedSettings,
      baseLambdaEndpoint: defaultEndpoint.baseLambdaEndpoint,
    };

    setSavedSettings(newSettings);
    setPendingSettings(newSettings);

    // Show reload popup if endpoint actually changed
    if (currentSavedSettings.baseLambdaEndpoint !== defaultEndpoint.baseLambdaEndpoint) {
      if (
        window.confirm(
          'Base Lambda Endpoint has been restored to default. Would you like to reload the page to apply the changes?',
        )
      ) {
        window.location.reload();
      }
    }
  };

  const onSave = () => {
    const previousSettings = settingsService.getSettings();

    // Apply all pending changes
    settingsService.updateSettings(pendingSettings);
    setSavedSettings(pendingSettings);

    // Check if base lambda endpoint changed and show reload popup
    if (LEVEL !== 'production' && previousSettings.baseLambdaEndpoint !== pendingSettings.baseLambdaEndpoint) {
      if (
        window.confirm('Base Lambda Endpoint has been changed. Would you like to reload the page to apply the changes?')
      ) {
        window.location.reload();
        return;
      }
    }

    // Close modal if no reload needed
    closeModal();
  };

  const onCancel = () => {
    // Reset pending changes to saved settings
    setPendingSettings(savedSettings);
    closeModal();
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    pendingSettings.microphoneId !== savedSettings.microphoneId ||
    pendingSettings.speakerId !== savedSettings.speakerId ||
    pendingSettings.baseLambdaEndpoint !== savedSettings.baseLambdaEndpoint;

  useEffect(() => {
    requestPermission();
  }, [requestPermission]);

  const microphoneOptions = useMemo(
    () =>
      microphoneDevices.map((device) => ({
        label: device.label,
        value: device.deviceId,
      })),
    [microphoneDevices],
  );

  const speakerOptions = useMemo(
    () =>
      speakerDevices.map((device) => ({
        label: device.label,
        value: device.deviceId,
      })),
    [speakerDevices],
  );

  const selectedMicrophone = useMemo(
    () => microphoneOptions.find((option) => option.value === pendingSettings.microphoneId),
    [microphoneOptions, pendingSettings.microphoneId],
  );

  const selectedSpeaker = useMemo(
    () => speakerOptions.find((option) => option.value === pendingSettings.speakerId),
    [speakerOptions, pendingSettings.speakerId],
  );

  return (
    <PopUp isVisible={modal.visible} maxWidth="small" onChangeVisible={closeModal}>
      <Dialog
        cancelButtonText="Cancel"
        isVisible={modal.visible}
        onDismiss={hasUnsavedChanges ? onCancel : closeModal}
        onSubmit={onSave}
        submitButtonFlavor="primary"
        submitButtonText="Save"
        submitDisabled={!hasUnsavedChanges}
        title="Settings">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Label htmlFor="microphoneDevice">Microphone Device:</Label>
            <div className="flex justify-between gap-2">
              <div className="flex-1">
                <Select
                  aria-label="Select microphone input device"
                  id="microphoneDevice"
                  isDisabled={microphoneDevices.length === 0 || isMicTesting}
                  onChange={(value: OptionType) => onSettingsChange('microphoneId', value)}
                  options={microphoneOptions}
                  placeholder="Select microphone device..."
                  value={selectedMicrophone} />
              </div>
              <Tooltip msg={isMicTesting ? 'Stop testing' : 'Test microphone'} position="top">
                <Button
                  disabled={!pendingSettings.microphoneId}
                  flavor={isMicTesting ? 'primary' : 'regular'}
                  isSquare
                  onClick={() => {
                    if (isMicTesting) {
                      stopMicTest();
                    } else {
                      testMicrophone(pendingSettings.microphoneId);
                    }
                  }}
                  size="large"
                  taste="line">
                  <MicrophoneIcon className="size-6 shrink-0" />
                </Button>
              </Tooltip>
            </div>

            <ProgressBar currentStep={volumeLevel} totalSteps={100} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="speakerDevice">Speaker Device:</Label>
            <div className="flex justify-between gap-2">
              <div className="flex-1">
                <Select
                  aria-label="Select speaker output device"
                  id="speakerDevice"
                  isDisabled={speakerDevices.length === 0 || isSpeakerTesting}
                  onChange={(value: OptionType) => onSettingsChange('speakerId', value)}
                  options={speakerOptions}
                  placeholder="Select speaker device..."
                  value={selectedSpeaker} />
              </div>
              <Tooltip msg="Play test sound" position="top">
                <Button
                  disabled={!pendingSettings.speakerId}
                  flavor={isSpeakerTesting ? 'primary' : 'regular'}
                  isSquare
                  onClick={() => testSpeaker(pendingSettings.speakerId)}
                  size="large"
                  taste="line">
                  <SpeakerWaveIcon className="size-6 shrink-0" />
                </Button>
              </Tooltip>
            </div>

            <ProgressBar currentStep={signalLevel} totalSteps={100} />
          </div>
          {LEVEL !== 'production' && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="baseLambdaEndpoint">Lambda Endpoint:</Label>
              <div className="flex justify-between gap-2 w-full">
                <Input
                  id="baseLambdaEndpoint"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange('baseLambdaEndpoint', e.target.value)}
                  placeholder="Enter base lambda endpoint..."
                  size="medium"
                  value={pendingSettings.baseLambdaEndpoint} />

                <Tooltip msg="Restore to default" position="top">
                  <Button
                    aria-label="Restore to default endpoint"
                    flavor="regular"
                    isSquare
                    onClick={onRestoreDefault}
                    size="large"
                    taste="line">
                    <ArrowUturnLeftIcon className="size-6 shrink-0" />
                  </Button>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </Dialog>
    </PopUp>
  );
};

export default SettingsModal;
